import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScannerState, Html5QrcodeScanType } from 'html5-qrcode';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface QRScannerProps {
    onScan: (data: string) => void;
    onError?: (error: string) => void;
    isActive: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScan, onError, isActive }) => {
    const scannerRef = useRef<Html5QrcodeScanner | null>(null);
    const elementRef = useRef<HTMLDivElement>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasPermission, setHasPermission] = useState<boolean | null>(null);
    const lastScanRef = useRef<string | null>(null);
    const lastScanTimeRef = useRef<number>(0);
    const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleScanSuccess = useCallback((decodedText: string, decodedResult: any) => {
        if (isProcessing) return;

        const currentTime = Date.now();

        // Prevent duplicate scans within 2 seconds
        if (lastScanRef.current === decodedText &&
            currentTime - lastScanTimeRef.current < 2000) {
            return;
        }

        setIsProcessing(true);
        lastScanRef.current = decodedText;
        lastScanTimeRef.current = currentTime;

        console.log('QR Code scanned:', decodedText);
        onScan(decodedText);

        // Clear processing state after 1.5 seconds
        if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
            setIsProcessing(false);
        }, 1500);
    }, [onScan, isProcessing]);

    const handleScanError = useCallback((errorMessage: string) => {
        // Only log actual errors, not the constant "No QR code found" messages
        if (!errorMessage.includes('No QR code found') &&
            !errorMessage.includes('QR code parse error')) {
            console.warn('QR Scanner error:', errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        }
    }, [onError]);

    const startScanner = useCallback(async () => {
        if (!elementRef.current || scannerRef.current) return;

        try {
            setError(null);
            setHasPermission(true);

            // Check if we're on mobile to prioritize back camera
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

            const scanner = new Html5QrcodeScanner(
                elementRef.current.id,
                {
                    fps: 10, // Optimized frame rate for performance
                    qrbox: { width: 250, height: 250 }, // Scanning area
                    aspectRatio: 1.0,
                    disableFlip: false,
                    supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
                    showTorchButtonIfSupported: true,
                    showZoomSliderIfSupported: false,
                    defaultZoomValueIfSupported: 1,
                    useBarCodeDetectorIfSupported: true, // Use native detector for better performance
                    experimentalFeatures: {
                        useBarCodeDetectorIfSupported: true
                    },
                    rememberLastUsedCamera: true,
                    formatsToSupport: undefined, // Support all formats
                    // Camera constraints to prefer back camera on mobile
                    videoConstraints: {
                        facingMode: isMobile ? { exact: "environment" } : { ideal: "environment" }, // Force back camera on mobile
                        width: { ideal: 1280, max: 1920 },
                        height: { ideal: 720, max: 1080 }
                    }
                },
                false // verbose logging
            );

            scannerRef.current = scanner;

            // Try to render with back camera first
            try {
                scanner.render(handleScanSuccess, handleScanError);
                setIsScanning(true);
            } catch (cameraError: any) {
                // If back camera fails on mobile, try with ideal constraint instead of exact
                if (isMobile && cameraError.message?.includes('facingMode')) {
                    console.log('Back camera not available, trying with ideal constraint...');
                    scanner.clear();

                    const fallbackScanner = new Html5QrcodeScanner(
                        elementRef.current.id,
                        {
                            fps: 10,
                            qrbox: { width: 250, height: 250 },
                            aspectRatio: 1.0,
                            disableFlip: false,
                            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
                            showTorchButtonIfSupported: true,
                            showZoomSliderIfSupported: false,
                            defaultZoomValueIfSupported: 1,
                            useBarCodeDetectorIfSupported: true,
                            experimentalFeatures: {
                                useBarCodeDetectorIfSupported: true
                            },
                            rememberLastUsedCamera: true,
                            formatsToSupport: undefined,
                            videoConstraints: {
                                facingMode: { ideal: "environment" }, // Fallback to ideal
                                width: { ideal: 1280, max: 1920 },
                                height: { ideal: 720, max: 1080 }
                            }
                        },
                        false
                    );

                    scannerRef.current = fallbackScanner;
                    fallbackScanner.render(handleScanSuccess, handleScanError);
                    setIsScanning(true);
                } else {
                    throw cameraError;
                }
            }

        } catch (err: any) {
            console.error('Failed to start QR scanner:', err);
            setError(err.message || 'Failed to start scanner');
            setHasPermission(false);
            if (onError) {
                onError(err.message || 'Failed to start scanner');
            }
        }
    }, [handleScanSuccess, handleScanError, onError]);

    const stopScanner = useCallback(() => {
        if (scannerRef.current) {
            try {
                const state = scannerRef.current.getState();
                if (state === Html5QrcodeScannerState.SCANNING ||
                    state === Html5QrcodeScannerState.PAUSED) {
                    scannerRef.current.clear();
                }
            } catch (error) {
                console.warn('Error stopping scanner:', error);
            }
            scannerRef.current = null;
        }

        setIsScanning(false);
        setIsProcessing(false);

        // Clear processing timeout
        if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
        }

        // Reset scan tracking
        lastScanRef.current = null;
        lastScanTimeRef.current = 0;

        // Clear the scanner element
        if (elementRef.current) {
            elementRef.current.innerHTML = '';
        }
    }, []);

    useEffect(() => {
        if (isActive) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                startScanner();
            }, 100);
            return () => clearTimeout(timer);
        } else {
            stopScanner();
        }
    }, [isActive, startScanner, stopScanner]);

    useEffect(() => {
        return () => {
            stopScanner();
        };
    }, [stopScanner]);

    if (!isActive) {
        return null;
    }

    if (hasPermission === false) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 text-center">
                <XMarkIcon className="w-8 h-8 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-red-800 mb-2">
                    Camera Access Denied
                </h3>
                <p className="text-sm sm:text-base text-red-600 mb-4">
                    Please allow camera access to scan QR codes
                </p>
                <button
                    onClick={startScanner}
                    className="btn-primary text-sm sm:text-base px-4 py-2"
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 text-center">
                <XMarkIcon className="w-8 h-8 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-red-800 mb-2">
                    Scanner Error
                </h3>
                <p className="text-sm sm:text-base text-red-600 mb-4">{error}</p>
                <button
                    onClick={startScanner}
                    className="btn-primary text-sm sm:text-base px-4 py-2"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="relative w-full">
            {/* Status indicator */}
            {isScanning && (
                <div className="mb-4 flex items-center justify-center">
                    <div className="flex items-center space-x-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg border border-blue-200">
                        <CameraIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">
                            {isProcessing ? '⚡ Processing QR Code...' : 'Scanning for QR codes'}
                        </span>
                        {!isProcessing && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                    </div>
                </div>
            )}

            {/* Success indicator */}
            {isProcessing && (
                <div className="mb-4 flex items-center justify-center">
                    <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-200">
                        <span className="text-sm font-medium">✅ QR Code Detected Successfully!</span>
                    </div>
                </div>
            )}

            {/* Scanner container */}
            <div
                id="qr-scanner-container"
                ref={elementRef}
                className="w-full"
                style={{
                    minHeight: '400px'
                }}
            />

            {/* Instructions */}
            <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                    Point your camera at a QR code to scan it
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    Make sure the QR code is well-lit and clearly visible
                </p>
            </div>
        </div>
    );
};

export default QRScanner;